// Abonnemang (steg 10.8, ADR 0120): paketet, månadens automatiska offerter och
// vägen till Stripe för kort, byte av paket, uppsägning och kvitton.
import { planById } from '@leadledger/domain';
import { Link } from 'react-router';
import { useBetalning } from '../api/queries.js';
import { useMe } from '../auth/auth.js';
import { Sida } from '../layout/Sida.js';
import { Kundportal } from './betalning/BetalningKlar.js';

const datum = new Intl.DateTimeFormat('sv-SE', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'Europe/Stockholm',
});

const manadsnamn = new Intl.DateTimeFormat('sv-SE', { month: 'long', timeZone: 'UTC' });

/** "2026-10" blir "oktober". */
export function manadText(manad: string): string {
  const [ar, man] = manad.split('-').map(Number) as [number, number];
  return manadsnamn.format(new Date(Date.UTC(ar, man - 1, 15)));
}

export function Abonnemang() {
  const me = useMe();
  const betalning = useBetalning();
  const agare = me.user.role === 'owner';

  if (betalning.isPending) {
    return (
      <Sida titel="Abonnemang">
        <p className="hjalp">Hämtar abonnemanget …</p>
      </Sida>
    );
  }
  if (betalning.isError) {
    return (
      <Sida titel="Abonnemang">
        <p className="meddelande" role="alert">
          Det gick inte att hämta abonnemanget. Ladda om sidan om en stund.
        </p>
      </Sida>
    );
  }

  const { subscription: s, usage, paymentsEnabled } = betalning.data;
  if (!s || s.status === 'pending') {
    return (
      <Sida titel="Abonnemang">
        <section className="kort abonnemang">
          <h2>Inget paket än</h2>
          <p>
            Välj Solo eller Byrå och betala med kort, så ger formuläret automatiska offerter till
            era kunder.
          </p>
          {paymentsEnabled ? (
            <Link className="btn btn-p" to="/valj-plan">
              Välj plan och betala
            </Link>
          ) : (
            <a className="btn btn-p" href="mailto:info@leadledger.se">
              Kontakta oss
            </a>
          )}
        </section>
      </Sida>
    );
  }

  const plan = planById(s.plan);
  const lage = s.active
    ? s.status === 'past_due'
      ? 'Betalningen gick inte igenom. Stripe försöker igen, och allt fungerar som vanligt under tiden. Uppdatera kortet under Abonnemang och kvitton.'
      : s.cancelAtPeriodEnd && s.currentPeriodEnd
        ? `Uppsagt. Paketet gäller till ${datum.format(new Date(s.currentPeriodEnd))}.`
        : null
    : 'Abonnemanget är inte aktivt. Förfrågningar kommer in som meddelanden, utan automatiska offerter.';

  return (
    <Sida titel="Abonnemang">
      <section className="kort abonnemang" aria-labelledby="paket-rubrik">
        <h2 id="paket-rubrik">
          Paketet {plan.name}
          {s.manual ? '' : s.period === 'year' ? ', årsvis' : ', månadsvis'}
        </h2>
        {lage && (
          <p className={s.active ? 'kort notis' : 'meddelande'} role="status">
            {lage}
          </p>
        )}
        {usage && (
          <div className="forbrukning">
            <p id="forbrukning-text">
              <strong>
                {usage.used} av {usage.limit}
              </strong>{' '}
              automatiska offerter i {manadText(usage.month)}
            </p>
            <progress
              aria-labelledby="forbrukning-text"
              max={usage.limit}
              value={Math.min(usage.used, usage.limit)}
            />
            <p className="hjalp">
              Frågor och testläget räknas inte. Tar offerterna slut kommer förfrågningarna in som
              meddelanden, och ni svarar själva. Vi debiterar aldrig något extra.
            </p>
          </div>
        )}
        <ul className="granser">
          <li>{plan.users === 1 ? '1 användare' : `Upp till ${plan.users} användare`}</li>
          <li>{plan.forms === 1 ? '1 formulär' : `Upp till ${plan.forms} formulär`}</li>
          <li>{plan.senderDomain ? 'Utskick från er domän' : 'Utskick från LeadLedger'}</li>
        </ul>
        {!s.manual && s.currentPeriodEnd && !s.cancelAtPeriodEnd && s.active && (
          <p>Nästa betalning {datum.format(new Date(s.currentPeriodEnd))}.</p>
        )}
        {s.manual ? (
          <p className="hjalp">
            Ert abonnemang hanteras av LeadLedger. Kontakta oss på{' '}
            <a href="mailto:info@leadledger.se">info@leadledger.se</a> för ändringar.
          </p>
        ) : agare ? (
          <Kundportal />
        ) : (
          <p className="hjalp">Bara byråns ägare kan byta paket, kort eller säga upp.</p>
        )}
        {!s.active && agare && paymentsEnabled && (
          <Link className="btn btn-p" to="/valj-plan">
            Välj plan och betala
          </Link>
        )}
      </section>
    </Sida>
  );
}
